import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Custom hook for auto-saving form data with debouncing
 * @param {Function} onUpdate - Function to call when saving data
 * @param {Function} validateData - Optional validation function
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 1000)
 * @returns {Object} - Hook utilities and state
 */
export const useAutoSave = (onUpdate, validateData = null, debounceMs = 2000) => {
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [errors, setErrors] = useState({});
  const debounceTimeoutRef = useRef(null);

  // Auto-save function with proper debouncing
  const autoSave = useCallback((data) => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout for debounced save
    debounceTimeoutRef.current = setTimeout(async () => {
      // Validate data if validator is provided
      if (validateData) {
        const validationErrors = validateData(data);
        if (Object.keys(validationErrors).length > 0) {
          setErrors(validationErrors);
          return;
        }
      }

      try {
        setAutoSaving(true);
        setErrors({});
        await onUpdate(data);
        setLastSaved(new Date());
      } catch (error) {
        console.error('Error auto-saving:', error);
        setErrors({ general: 'Failed to save changes automatically' });
      } finally {
        setAutoSaving(false);
      }
    }, debounceMs);
  }, [onUpdate, validateData, debounceMs]);

  // Clear specific field error
  const clearFieldError = useCallback((field) => {
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  }, [errors]);

  // Clear all errors
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  // Set errors manually
  const setFieldErrors = useCallback((newErrors) => {
    setErrors(newErrors);
  }, []);

  // Cleanup function to cancel pending saves
  const cleanup = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    autoSaving,
    lastSaved,
    errors,
    autoSave,
    clearFieldError,
    clearErrors,
    setFieldErrors,
    cleanup
  };
};

export default useAutoSave;
